// ===========================================
// Parent Events Widget
// ===========================================
// Dashboard widget showing the next 2-3 upcoming events.
// Drop into ParentDashboardPage Overview tab.

import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  Stack,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  ArrowForward as ArrowIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import { useParentEvents } from '../../hooks/useEventsParent';
import { useTenant } from '../../contexts/TenantContext';
import { ParentEventListItem } from '../../api/events.api';
import { EVENT_TYPE_COLORS } from '../../constants/eventTypes';
import { minTierPriceCents } from '../events/EventTierPricing';

// ===========================================
// SINGLE EVENT ROW
// ===========================================

interface EventRowProps {
  event: ParentEventListItem;
  onClick: () => void;
}

function EventRow({ event, onClick }: EventRowProps) {
  const startDate = new Date(event.startDateTime);
  const typeColor = EVENT_TYPE_COLORS[event.type] ?? '#4580E4';

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`View event: ${event.title}`}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 1,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        borderLeft: `3px solid ${typeColor}`,
        pl: 1.5,
      }}
    >
      {/* Date badge */}
      <Box
        sx={{
          minWidth: 40,
          textAlign: 'center',
          backgroundColor: typeColor,
          color: '#fff',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" display="block" lineHeight={1.2} fontWeight={700}>
          {format(startDate, 'd')}
        </Typography>
        <Typography variant="caption" display="block" lineHeight={1} sx={{ fontSize: '0.6rem' }}>
          {format(startDate, 'MMM')}
        </Typography>
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          title={event.title}
        >
          {event.title}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <EventIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {format(startDate, 'h:mm a')}
          </Typography>
          {event.venueName && (
            <>
              <LocationIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {event.venueName}
              </Typography>
            </>
          )}
        </Stack>
      </Box>

      {/* Free / paid badge — cheapest tier price, falling back to the legacy field */}
      {!event.isFree ? (
        <Chip
          icon={<TicketIcon sx={{ fontSize: '0.75rem !important' }} />}
          label={`$${(((minTierPriceCents(event.EventTicketTier) ?? event.ticketPriceCents) ?? 0) / 100).toFixed(0)}`}
          size="small"
          sx={{ backgroundColor: '#FFCE00', color: '#080808', fontWeight: 600, height: 20, fontSize: '0.65rem' }}
        />
      ) : (
        <Chip
          label="Free"
          size="small"
          sx={{ backgroundColor: '#96DAC9', color: '#080808', fontWeight: 600, height: 20, fontSize: '0.65rem' }}
        />
      )}
    </Box>
  );
}

// ===========================================
// WIDGET
// ===========================================

export default function ParentEventsWidget() {
  const { schoolSlug } = useTenant();
  const navigate = useNavigate();

  const { data, isLoading } = useParentEvents({ limit: 3 });
  const events: ParentEventListItem[] = data?.data ?? [];

  const handleViewAll = () => {
    navigate(schoolSlug ? `/${schoolSlug}/parent/events` : 'events');
  };

  const handleEventClick = (id: string) => {
    navigate(schoolSlug ? `/${schoolSlug}/parent/events/${id}` : `events/${id}`);
  };

  return (
    <Card elevation={0} sx={{ boxShadow: 'none' }}>
      <CardContent sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6">
            <EventIcon sx={{ mr: 0.75, verticalAlign: 'middle', color: 'primary.main' }} />
            Upcoming Events
          </Typography>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No upcoming events.
          </Alert>
        ) : (
          <Stack spacing={0.75}>
            {events.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                onClick={() => handleEventClick(event.id)}
              />
            ))}
          </Stack>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 1 }}>
        <Button
          size="small"
          endIcon={<ArrowIcon />}
          onClick={handleViewAll}
        >
          View all events
        </Button>
      </CardActions>
    </Card>
  );
}
